import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import debounce from 'debounce';
import {
    StyledInput,
    StyledSelect,
    StyledLabel,
    StyledCheckbox,
    StyledRange,
    FormGroup
} from '../../../../../shared/layout/forms/FormElements';

const OverlayControlEditor = ({ fieldSetting, index, onChange }) => {
    const [localFieldSetting, setLocalFieldSetting] = useState({ ...fieldSetting });

    // Debounced input handler for parent component
    const debouncedHandleInputChange = useCallback(
        debounce((field, value) => {
            onChange(index, field, value);
        }, 300), // 300ms delay
        [index, onChange]
    );

    const handleInputChange = (field, value) => {
        setLocalFieldSetting((prev) => ({
            ...prev,
            [field]: value,
        }));
        debouncedHandleInputChange(field, value);
    };

    // Sync local state with prop updates
    useEffect(() => {
        setLocalFieldSetting({ ...fieldSetting });
    }, [fieldSetting]);

    const positionValue = localFieldSetting.position ?? 0;

    return (
        <Card>
            <CardHeader>
                <FieldName>{localFieldSetting.overlay_field?.name || 'Unnamed field'}</FieldName>
                <Badge>{localFieldSetting.overlay_field?.key || 'custom'}</Badge>
            </CardHeader>
            <ControlGrid>
                <FormGroup $noMargin>
                    <StyledLabel>Font family</StyledLabel>
                    <StyledSelect
                        value={localFieldSetting.font_name}
                        onChange={(e) => handleInputChange('font_name', e.target.value)}
                    >
                        <option value='Arial'>Arial</option>
                        <option value='Verdana'>Verdana</option>
                        <option value='Tahoma'>Tahoma</option>
                        <option value='Georgia'>Georgia</option>
                        <option value='Times New Roman'>Times New Roman</option>
                    </StyledSelect>
                </FormGroup>

                <FormGroup $noMargin>
                    <StyledLabel>Font size</StyledLabel>
                    <StyledSelect
                        value={localFieldSetting.font_size}
                        onChange={(e) => handleInputChange('font_size', e.target.value)}
                    >
                        <option value='small'>Small</option>
                        <option value='medium'>Medium</option>
                        <option value='large'>Large</option>
                    </StyledSelect>
                </FormGroup>

                <FormGroup $noMargin>
                    <StyledLabel>Alignment</StyledLabel>
                    <StyledSelect
                        value={localFieldSetting.alignment}
                        onChange={(e) => handleInputChange('alignment', e.target.value)}
                    >
                        <option value='left'>Left</option>
                        <option value='center'>Center</option>
                        <option value='right'>Right</option>
                    </StyledSelect>
                </FormGroup>

                <FormGroup $noMargin>
                    <StyledLabel>Font color</StyledLabel>
                    <StyledInput
                        type='color'
                        value={localFieldSetting.font_color}
                        onChange={(e) => handleInputChange('font_color', e.target.value)}
                        style={{ padding: 2, height: '2.875rem' }}
                    />
                </FormGroup>

                <div style={{ gridColumn: 'span 2' }}>
                    <FormGroup $noMargin>
                        <StyledLabel>Vertical position ({Math.round(positionValue)}%)</StyledLabel>
                        <SliderRow>
                            <StyledRange
                                type='range'
                                value={positionValue}
                                onChange={(e) => handleInputChange('position', e.target.value)}
                                min='0'
                                max='100'
                            />
                            <StyledInput
                                type='number'
                                value={positionValue}
                                min='0'
                                max='100'
                                onChange={(e) => handleInputChange('position', e.target.value)}
                                style={{ width: '5rem' }}
                            />
                        </SliderRow>
                    </FormGroup>
                </div>

                <FormGroup $noMargin>
                    <StyledLabel>Transition</StyledLabel>
                    <StyledSelect
                        value={localFieldSetting.transition}
                        onChange={(e) => handleInputChange('transition', e.target.value)}
                    >
                        <option value='none'>None</option>
                        <option value='fade'>Fade</option>
                        <option value='slide'>Slide</option>
                        <option value='zoom'>Zoom</option>
                    </StyledSelect>
                </FormGroup>

                <FormGroup $noMargin>
                    <StyledLabel>Duration (ms)</StyledLabel>
                    <StyledInput
                        type='number'
                        min='0'
                        max='5000'
                        value={localFieldSetting.transition_duration}
                        onChange={(e) =>
                            handleInputChange('transition_duration', e.target.value)
                        }
                    />
                </FormGroup>

                <FormGroup $noMargin>
                    <StyledLabel>Delay (ms)</StyledLabel>
                    <StyledInput
                        type='number'
                        min='0'
                        max='5000'
                        value={localFieldSetting.transition_delay}
                        onChange={(e) =>
                            handleInputChange('transition_delay', e.target.value)
                        }
                    />
                </FormGroup>
            </ControlGrid>

            <CheckboxLabel>
                <StyledCheckbox
                    checked={localFieldSetting.visible}
                    onChange={(e) => handleInputChange('visible', e.target.checked)}
                />
                Visible on overlay
            </CheckboxLabel>
        </Card>
    );
};

export default OverlayControlEditor;

const Card = styled.div`
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    padding: 1rem;
    background: #fff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
    margin-bottom: 1rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
`;

const CardHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 0.75rem;
`;

const FieldName = styled.h6`
    margin: 0;
    font-size: 1rem;
    font-weight: 600;
    color: #111827;
`;

const Badge = styled.span`
    padding: 0.25rem 0.625rem;
    border-radius: 20px;
    background: #fdf4ff;
    color: #821692;
    font-size: 0.75rem;
    font-weight: 600;
`;

const ControlGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 1rem;
`;

const SliderRow = styled.div`
    display: flex;
    align-items: center;
    gap: 0.75rem;
`;

const CheckboxLabel = styled.label`
    display: inline-flex;
    align-items: center;
    font-size: 0.95rem;
    font-weight: 500;
    color: #374151;
    cursor: pointer;
`;
